import { NavigateFunction } from 'react-router-dom';
import { getLanguagePath } from './utils';

export type PlanId = 'quarterly' | 'annual';

// Map plan IDs to Stripe lookup keys
export const getStripeLookupKey = (planId: PlanId): string => {
  switch (planId) {
    case 'quarterly':
      return import.meta.env.VITE_STRIPE_LOOKUP_QUARTERLY as string;
    case 'annual':
      return import.meta.env.VITE_STRIPE_LOOKUP_ANNUAL as string;
    default:
      throw new Error(`Invalid plan ID: ${planId}`);
  }
};

async function checkout(
  userId: string | undefined,
  onUnauthorized: () => void,
  navigate: NavigateFunction,
  allowLoginOptional: boolean = false,
  searchParams?: string
) {

  // Check if login is required based on mode
  const checkoutMode = import.meta.env.VITE_CHECKOUT_MODE || 'login_required';

  if (!userId && (checkoutMode === 'login_required' || !allowLoginOptional)) {
    onUnauthorized();
    return;
  }

  try {
    // Navigate to checkout page
    const checkoutPath = getLanguagePath('checkout') + (searchParams || '');
    navigate(checkoutPath);
  } catch (error) {
    console.error('Error during checkout:', error);
    // You might want to show a toast notification here
  }
}

interface CheckoutFlowOptions {
  planId: string;
  userId: string | undefined;
  navigate: NavigateFunction;
  setIsLoading?: (isLoading: boolean) => void;
  skipConfirmation?: boolean;
  allowLoginOptional?: boolean;
  searchParams?: string;
}

export async function handleCheckout({ planId, userId, navigate, setIsLoading = undefined, skipConfirmation = true, allowLoginOptional = false, searchParams }: CheckoutFlowOptions) {
  setIsLoading?.(true);
  console.log('handleCheckout', { planId, userId, skipConfirmation });

  if (planId !== 'quarterly' && planId !== 'annual') {
    console.error('Invalid plan ID:', planId);
    return;
  }

  try {
    localStorage.setItem('pendingPurchasePlanId', planId);
    if (!skipConfirmation && userId) {
      // Store current path in localStorage before redirecting to confirmation
      localStorage.setItem('authRedirectTo', window.location.pathname);
      navigate(getLanguagePath('confirm-account'));
      return;
    }

    await checkout(
      userId?.toUpperCase(),
      () => {
        localStorage.setItem('authRedirectTo', window.location.pathname);
        localStorage.setItem('pendingPurchasePlanId', planId);
        navigate(getLanguagePath('sign-in'));
      },
      navigate,
      allowLoginOptional,
      searchParams
    );
  } finally {
    setIsLoading?.(false);
  }
}